// screens/create-product-mgt/AllocateIngredients.jsx
import React, { useState, useEffect } from 'react';
import { Form, Row, Col, Button, Table, Card, Alert, Spinner, Badge, InputGroup, Modal } from 'react-bootstrap';
import Select from 'react-select';
import { toast } from 'react-toastify';
import axios from '../../api/axios';

const AllocateIngredients = ({ data, onNext, onBack, updateData, currentStep, isSubmitting, setIsSubmitting, setErrors, onComplete }) => {
    const accessToken = localStorage.getItem("authToken");

    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isUpdating, setIsUpdating] = useState(false);
    const [categoryList, setCategoryList] = useState([]);
    const [addons, setAddons] = useState([]);
    const [rawMaterialList, setRawMaterialList] = useState([]);
    const [showSkipConfirm, setShowSkipConfirm] = useState(false);
    const [showCompleteConfirm, setShowCompleteConfirm] = useState(false);
    const [existingIngredients, setExistingIngredients] = useState(null);
    const [formData, setFormData] = useState({
        product: '',
        rawMaterials: [{
            rawMaterial: '',
            quantity: '',
            unit: '',
            addonId: '',
            addonValueId: '',
            isImportant: false
        }]
    });

    const productData = data.product || {};
    const productId = productData._id;

    useEffect(() => {
        getCategoryList();
        getAddons();
        getRawMaterials();

        // Check if ingredients already exist for this product
        if (productId) {
            setFormData(prev => ({
                ...prev,
                product: productId
            }));
            fetchExistingIngredients();
        }
    }, [productId]);

    const getCategoryList = async () => {
        try {
            const headers = {
                Authorization: `Bearer ${accessToken}`,
            };
            const response = await axios.get("masters/category/get", { headers });

            if (response?.data?.status === true) {
                setCategoryList(response?.data?.data?.categories);
            }
        } catch (error) {
            toast.error("Failed to load categories", {
                position: "top-right",
                autoClose: 3000,
                theme: "light",
            });
        }
    };

    const getAddons = async () => {
        try {
            const headers = {
                Authorization: `Bearer ${accessToken}`
            };
            const response = await axios.get("addons?page=1&limit=1000", { headers });
            setAddons(response?.data?.data?.addons || []);
        } catch (error) {
            console.error("Error fetching addons:", error);
        }
    };

    const getRawMaterials = async () => {
        try {
            const headers = {
                Authorization: `Bearer ${accessToken}`,
            };
            const response = await axios.get("raw-material", { headers });

            if (response?.data?.status === true) {
                const materials = response?.data?.data?.rawMaterials || [];
                materials.forEach((item, index) => {
                    item.serialNumber = index + 1;
                });
                setRawMaterialList(materials);
            }
        } catch (error) {
            toast.error("Failed to load raw materials", {
                position: "top-right",
                autoClose: 3000,
                theme: "light",
            });
        }
    };

    const fetchExistingIngredients = async () => {
        if (!productId) return;
        
        try {
            setIsLoading(true);
            const headers = {
                Authorization: `Bearer ${accessToken}`,
            };
            
            // Fetch recipes for this specific product
            const response = await axios.get(`recipe?product=${productId}`, { headers });
            
            if (response?.data?.status === true && response?.data?.data?.recieps?.length > 0) {
                const existingRecipe = response.data.data.recieps[0];
                setExistingIngredients(existingRecipe);
                
                // Pre-fill form with existing ingredients
                const mappedRawMaterials = existingRecipe?.rawMaterials?.map(item => ({
                    _id: item?._id,
                    rawMaterial: item?.rawMaterial?._id,
                    quantity: item?.quantity || '',
                    unit: item?.rawMaterial?.unit || '',
                    addonId: item?.addonId?._id || '',
                    addonValueId: item?.addonValueId || '',
                    isImportant: item?.isImportant || false
                })) || [{
                    rawMaterial: '',
                    quantity: '',
                    unit: '',
                    addonId: '',
                    addonValueId: '',
                    isImportant: false
                }];
                
                setFormData({
                    _id: existingRecipe?._id,
                    product: productId,
                    rawMaterials: mappedRawMaterials
                });
                
                toast.info('Existing ingredients loaded. You can update them.', {
                    position: "top-right",
                    autoClose: 3000,
                    theme: "light",
                });
            }
        } catch (error) {
            console.error('Error fetching existing ingredients:', error);
            // No ingredients exist yet - that's fine
        } finally {
            setIsLoading(false);
        }
    };

    const handleRawMaterialChange = (index, field, value) => {
        const updatedRawMaterials = [...formData.rawMaterials];
        updatedRawMaterials[index] = {
            ...updatedRawMaterials[index],
            [field]: value
        };

        // If rawMaterial changes, update the unit as well
        if (field === 'rawMaterial' && value) {
            const selectedMaterial = rawMaterialList.find(rm => rm._id === value);
            updatedRawMaterials[index].unit = selectedMaterial?.unit || "";

            // Clear addon fields when raw material changes
            updatedRawMaterials[index].addonId = "";
            updatedRawMaterials[index].addonValueId = "";
        }

        // If addonId changes, clear the addonValueId
        if (field === 'addonId') {
            updatedRawMaterials[index].addonValueId = "";
        } 

        setFormData({
            ...formData,
            rawMaterials: updatedRawMaterials
        });
    };

    const addRawMaterialEntry = () => {
        setFormData(prev => ({
            ...prev,
            rawMaterials: [
                ...prev.rawMaterials,
                {
                    rawMaterial: '',
                    quantity: '',
                    unit: '',
                    addonId: '',
                    addonValueId: '',
                    isImportant: false
                }
            ],
        }));
    };

    const removeRawMaterialEntry = (index) => {
        setFormData(prev => {
            const newRawMaterials = [...prev.rawMaterials];
            newRawMaterials.splice(index, 1);

            // Ensure we always have at least one raw material entry
            if (newRawMaterials.length === 0) {
                newRawMaterials.push({
                    rawMaterial: '',
                    quantity: '',
                    unit: '',
                    addonId: '',
                    addonValueId: '',
                    isImportant: false
                });
            }

            return {
                ...prev,
                rawMaterials: newRawMaterials
            };
        });
    };

    const validateForm = () => {
        // Check if product ID exists
        if (!productId) {
            toast.error('Product not created yet. Please go back to Step 1.', { position: 'top-right' });
            return false;
        }

        // Check if at least one raw material is selected
        const hasValidEntries = formData.rawMaterials.some(rm => rm.rawMaterial);
        if (!hasValidEntries) {
            toast.error('Please add at least one raw material or skip ingredients', { position: 'top-right' });
            return false;
        }

        // Validate each raw material entry
        for (let i = 0; i < formData.rawMaterials.length; i++) {
            const rm = formData.rawMaterials[i];

            // Skip validation for empty entries
            if (!rm.rawMaterial && !rm.quantity) {
                continue;
            }

            if (!rm.rawMaterial) {
                toast.error(`Please select a raw material for entry ${i + 1}`, { position: 'top-right' });
                return false;
            }

            if (!rm.quantity || parseFloat(rm.quantity) <= 0) {
                toast.error(`Please enter a valid quantity for entry ${i + 1}`, { position: 'top-right' });
                return false;
            }

            // If addonId is selected, addonValueId must also be selected
            if (rm.addonId && !rm.addonValueId) {
                toast.error(`Please select an addon value for entry ${i + 1} when addon is selected`, { position: 'top-right' });
                return false;
            }
        }

        return true;
    };

    const handleSkipIngredients = () => {
        setShowSkipConfirm(true);
    };

    const confirmSkipIngredients = async () => {
        setShowSkipConfirm(false);
        
        if (!productId) {
            toast.error('Product not created yet. Please go back to Step 1.', { position: 'top-right' });
            return;
        }

        setIsSaving(true);
        setIsSubmitting(true);

        try {
            // Store empty ingredients data
            updateData('ingredients', {
                items: [],
                payload: null,
                totalIngredients: 0,
                isAllocated: false,
                skipped: true
            });

            toast.success('Product ingredients step skipped!', {
                position: "top-right",
                autoClose: 3000,
                theme: "light",
            });

            // Proceed to next or complete
            if (onComplete) {
                onComplete();
            }

        } catch (error) {
            console.error('Error skipping ingredients:', error);
            toast.error('Failed to skip ingredients', {
                position: "top-right",
                autoClose: 3000,
                theme: "light",
            });
        } finally {
            setIsSaving(false);
            setIsSubmitting(false);
        }
    };

    const handleSaveOrUpdateIngredients = async () => {
        if (!validateForm()) {
            return;
        }

        // Prepare payload with only valid entries
        const validRawMaterials = formData.rawMaterials.filter(rm => 
            rm.rawMaterial && rm.quantity && parseFloat(rm.quantity) > 0
        );

        if (validRawMaterials.length === 0) {
            toast.error('Please add at least one valid raw material', { position: 'top-right' });
            return;
        }

        const payload = {
            product: productId,
            rawMaterials: validRawMaterials.map(rm => ({
                rawMaterial: rm.rawMaterial,
                quantity: rm.quantity,
                unit: rm.unit,
                addonId: rm.addonId || null,
                addonValueId: rm.addonValueId || null,
                isImportant: rm.isImportant || false
            }))
        };

        // If we have an existing recipe ID, include it for update
        if (formData._id) {
            payload._id = formData._id;
        }

        // Sanitize addonId and addonValueId - send null if empty
        payload.rawMaterials = payload.rawMaterials.map(item => ({
            ...item,
            addonId: item.addonId?.toString().trim() === "" ? null : item.addonId,
            addonValueId: item.addonValueId?.toString().trim() === "" ? null : item.addonValueId
        }));

        // Validate addon pairs
        const invalidItem = payload.rawMaterials.find(item =>
            item.addonId?.toString().trim() !== "" && (!item.addonValueId || item.addonValueId.toString().trim() === "")
        );

        // if (invalidItem) {
        //     toast.error("If an Addon is selected, Addon Value must also be selected.", {
        //         position: "top-right",
        //         autoClose: 3000,
        //         theme: "light",
        //     });
        //     return;
        // }

        setIsUpdating(true);
        setIsSubmitting(true);

        try {
            const headers = {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${accessToken}`
            };

            // Use PUT for update if we have _id, otherwise POST for create
            const endpoint = "recipe";
            const method = payload._id ? 'put' : 'post';
            
            console.log(`Sending ${method.toUpperCase()} request to ${endpoint}:`, payload);
            
            const response = await axios[method](endpoint, payload, { headers });

            if (response?.data?.status === true) {
                const message = payload._id ? 'Ingredients updated successfully!' : 'Ingredients saved successfully!';
                
                toast.success(message, {
                    position: "top-right",
                    autoClose: 3000,
                    theme: "light",
                });

                // Store ingredients data
                updateData('ingredients', {
                    items: validRawMaterials,
                    payload: payload,
                    totalIngredients: validRawMaterials.length,
                    isAllocated: true,
                    skipped: false,
                    recipeId: response.data.data?.dbResponse?._id || formData._id
                });

                // Show completion confirmation
                setShowCompleteConfirm(true);

            } else {
                toast.error(response?.data?.error || 'Failed to save ingredients', {
                    position: "top-right",
                    autoClose: 3000,
                    theme: "light",
                });
            }
        } catch (error) {
            console.error('Error saving/updating ingredients:', error);

            let errorMessage = 'Failed to save ingredients';
            if (error.response) {
                if (error.response.status === 404) {
                    errorMessage = error.response.data.error?.message || error.response.data.error;
                } else if (error.response.status === 400) {
                    errorMessage = error.response.data.data?.message || error.response.data.error;
                } else if (error.response.status === 406) {
                    errorMessage = error.response.data.data?.message || error.response.data.error;
                } else if (error.response.status === 412) {
                    errorMessage = error.response.data.data?.message || error.response.data.error;
                } else if (error.response.status === 500) {
                    errorMessage = error.response.data.error;
                }
            }

            toast.error(errorMessage, {
                position: "top-right",
                autoClose: 3000,
                theme: "light",
            });
            setErrors([errorMessage]);
        } finally {
            setIsUpdating(false);
            setIsSubmitting(false);
        }
    };

    const confirmComplete = async () => {
        setShowCompleteConfirm(false);
        
        // Call the onComplete function to reset the wizard
        if (onComplete) {
            onComplete();
        }
    };

    const getAddonValueOptions = (addonId) => {
        if (!addonId) return [];
        const addon = addons.find(a => a._id === addonId);
        return addon?.addonValues?.map(val => ({
            value: val._id,
            label: val.value
        })) || [];
    };

    const rawMaterialOptions = rawMaterialList?.map(rm => ({
        value: rm._id,
        label: `${rm.name} (${rm.unit}) ${rm.inStock === false ? " - Out of stock" : ""}`
    }));

    const addonOptions = addons?.map(addon => ({
        value: addon._id,
        label: `${addon.title} ${addon.status === "ACTIVE" ? "✅" : "⏸️"}`
    }));

    // Calculate total valid ingredients
    const totalValidIngredients = formData.rawMaterials.filter(rm => 
        rm.rawMaterial && rm.quantity && parseFloat(rm.quantity) > 0
    ).length;

    return (
        <div className="allocate-ingredients-step">
            {/* Confirmation Modals */}
            <Modal show={showSkipConfirm} onHide={() => setShowSkipConfirm(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Skip Ingredients</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    Are you sure you want to skip adding ingredients? You can add them later from the product edit page.
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowSkipConfirm(false)}>
                        Cancel
                    </Button>
                    <Button variant="warning" onClick={confirmSkipIngredients}>
                        Yes, Skip Ingredients
                    </Button>
                </Modal.Footer>
            </Modal>

            <Modal show={showCompleteConfirm} onHide={() => setShowCompleteConfirm(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Complete Product Creation</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>Ingredients have been {formData._id ? 'updated' : 'saved'} successfully!</p>
                    <p>Do you want to complete the product creation process?</p>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowCompleteConfirm(false)}>
                        Continue Editing
                    </Button>
                    <Button variant="success" onClick={confirmComplete}>
                        Yes, Complete Creation
                    </Button>
                </Modal.Footer>
            </Modal>

            <h6 className="mb-4 text-primary">Step 4: Allocate Ingredients (Optional)</h6>

            {!productData._id && (
                <Alert variant="warning" className="mb-4">
                    <Alert.Heading>Product Not Created Yet</Alert.Heading>
                    <p>Please go back to Step 1 and create the product first to get the Product ID.</p>
                </Alert>
            )}

            <Card className="mb-4">
                <Card.Body>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <div>
                            <h6 className="mb-0 fw-semibold">Product Information</h6>
                            <p className="text-muted mb-0">{productData.name}</p>
                        </div>
                        <div>
                            {existingIngredients ? (
                                <Badge bg="info">
                                    <i className="ri-edit-line me-1"></i> Edit Existing Ingredients
                                </Badge>
                            ) : (
                                <Badge bg="primary">
                                    <i className="ri-add-line me-1"></i> Add New Ingredients
                                </Badge>
                            )}
                        </div>
                    </div>

                    <Row>
                        <Col md={6}>
                            <Form.Group>
                                <Form.Label>Product Name</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={productData.name || "Not created yet"}
                                    readOnly
                                />
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group>
                                <Form.Label>Status</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={existingIngredients ? "Ingredients exist - You can update them" : "No ingredients yet - Add new ones"}
                                    readOnly
                                />
                            </Form.Group>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            <Card>
                <Card.Body>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <div>
                            <h6 className="mb-0 fw-semibold">
                                {existingIngredients ? 'Update Ingredients' : 'Add Ingredients'} (Optional)
                            </h6>
                            <p className="text-muted mb-0">You can skip this step and add ingredients later</p>
                        </div>
                        <div>
                            <Badge bg={totalValidIngredients > 0 ? "success" : "secondary"}>
                                {totalValidIngredients} valid ingredient(s)
                            </Badge>
                            {existingIngredients && (
                                <Badge bg="warning" className="ms-2">
                                    Editing
                                </Badge>
                            )}
                        </div>
                    </div>

                    {isLoading ? (
                        <div className="text-center py-4">
                            <Spinner animation="border" variant="primary" />
                            <p className="mt-2">Loading existing ingredients...</p>
                        </div>
                    ) : (
                        <>
                            {formData.rawMaterials.map((entry, index) => (
                                <div key={index} className="mb-4 p-3 border rounded">
                                    <Row className="mb-3 align-items-center">
                                        <Col md={4}>
                                            <Form.Group>
                                                <Form.Label>
                                                    Raw Material <span className="text-danger">*</span>
                                                </Form.Label>
                                                <Select
                                                    options={rawMaterialOptions}
                                                    value={
                                                        entry.rawMaterial
                                                            ? {
                                                                value: entry.rawMaterial,
                                                                label: rawMaterialList.find(rm => rm._id === entry.rawMaterial)?.name
                                                            }
                                                            : null
                                                    }
                                                    onChange={(selectedOption) =>
                                                        handleRawMaterialChange(
                                                            index,
                                                            "rawMaterial",
                                                            selectedOption ? selectedOption.value : ""
                                                        )
                                                    }
                                                    placeholder="Select Raw Material"
                                                    classNamePrefix="react-select"
                                                />
                                            </Form.Group>
                                        </Col>

                                        <Col md={2}>
                                            <Form.Group>
                                                <Form.Label>
                                                    Quantity <span className="text-danger">*</span>
                                                </Form.Label>
                                                <Form.Control
                                                    type="number"
                                                    step="0.001"
                                                    value={entry.quantity || ''}
                                                    onChange={(e) =>
                                                        handleRawMaterialChange(index, "quantity", e.target.value)
                                                    }
                                                    placeholder="0.00"
                                                />
                                            </Form.Group>
                                        </Col>

                                        <Col md={2}>
                                            <Form.Group>
                                                <Form.Label>Unit</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    value={entry.unit || ''}
                                                    readOnly
                                                    placeholder="Auto-filled"
                                                />
                                            </Form.Group>
                                        </Col>

                                        <Col md={2} className="d-flex align-items-center pt-3">
                                            <Form.Check
                                                type="checkbox"
                                                id={`isImportant-${index}`}
                                                label="Important"
                                                checked={entry.isImportant || false}
                                                onChange={(e) =>
                                                    handleRawMaterialChange(
                                                        index,
                                                        "isImportant",
                                                        e.target.checked
                                                    )
                                                }
                                            />
                                        </Col>

                                        <Col md={2} className="text-end pt-3">
                                            {formData.rawMaterials.length > 1 && (
                                                <Button
                                                    variant="danger"
                                                    size="sm"
                                                    onClick={() => removeRawMaterialEntry(index)}
                                                >
                                                    Remove
                                                </Button>
                                            )}
                                        </Col>
                                    </Row>

                                    {/* Addon Fields */}
                                    <Row>
                                        <Col md={4}>
                                            <Form.Group>
                                                <Form.Label>Addon Group (Optional)</Form.Label>
                                                <Select
                                                    options={addonOptions}
                                                    value={
                                                        entry.addonId
                                                            ? {
                                                                value: entry.addonId,
                                                                label: addons.find(a => a._id === entry.addonId)?.title
                                                            }
                                                            : null
                                                    }
                                                    onChange={(selectedOption) =>
                                                        handleRawMaterialChange(
                                                            index,
                                                            "addonId",
                                                            selectedOption ? selectedOption.value : ""
                                                        )
                                                    }
                                                    placeholder="Select Addon Group"
                                                    classNamePrefix="react-select"
                                                    isClearable
                                                />
                                            </Form.Group>
                                        </Col>

                                        <Col md={4}>
                                            <Form.Group>
                                                <Form.Label>Addon Value (Optional)</Form.Label>
                                                <Select
                                                    options={getAddonValueOptions(entry.addonId)}
                                                    value={
                                                        entry.addonValueId && entry.addonId
                                                            ? {
                                                                value: entry.addonValueId,
                                                                label: getAddonValueOptions(entry.addonId)
                                                                    .find(v => v.value === entry.addonValueId)?.label || "Unknown Value"
                                                            }
                                                            : null
                                                    }
                                                    onChange={(selectedOption) =>
                                                        handleRawMaterialChange(
                                                            index,
                                                            "addonValueId",
                                                            selectedOption ? selectedOption.value : ""
                                                        )
                                                    }
                                                    placeholder={entry.addonId ? "Select Addon Value" : "Select Addon Group first"}
                                                    classNamePrefix="react-select"
                                                    isClearable
                                                    isDisabled={!entry.addonId}
                                                />
                                            </Form.Group>
                                        </Col>

                                        <Col md={4} className="d-flex align-items-end">
                                            <div className="text-muted small">
                                                {!entry.addonId && "Addon fields are optional"}
                                                {entry.addonId && !entry.addonValueId && "Please select addon value"}
                                                {entry.addonId && entry.addonValueId && "Addon configured"}
                                            </div>
                                        </Col>
                                    </Row>
                                </div>
                            ))}

                            <div className="text-center mt-3">
                                <Button
                                    variant="outline-primary"
                                    onClick={addRawMaterialEntry}
                                    className="me-2"
                                >
                                    <i className="ri-add-line me-1"></i> Add More Ingredients
                                </Button>
                            </div>
                        </>
                    )}

                    <Alert variant="info" className="mt-4">
                        <Alert.Heading>Note:</Alert.Heading>
                        <p className="mb-0">
                            {existingIngredients ? (
                                <>You are updating existing ingredients. Changes will be saved immediately.</>
                            ) : (
                                <>You are adding new ingredients. They will be saved when you click "Save Ingredients".</>
                            )}
                            <ul>
                                <li>Mark ingredients as "Important" if they are critical to the recipe</li>
                                <li>Addon fields are optional - use them if this ingredient depends on specific add-ons</li>
                                <li>If an addon group is selected, you must also select an addon value</li>
                            </ul>
                        </p>
                    </Alert>
                </Card.Body>
            </Card>

            <div className="mt-4 d-flex justify-content-between">
                <Button
                    variant="outline-secondary"
                    onClick={onBack}
                    disabled={isSaving || isSubmitting || isUpdating}
                >
                    ← Back to Add-ons
                </Button>
                
                <div>
                    <Button
                        variant="outline-warning"
                        onClick={handleSkipIngredients}
                        disabled={isSaving || isSubmitting || isUpdating || !productId}
                        className="me-3"
                    >
                        Skip Ingredients
                    </Button>
                    
                    <Button
                        variant={existingIngredients ? "primary" : "success"}
                        onClick={handleSaveOrUpdateIngredients}
                        disabled={isSaving || isSubmitting || isUpdating || !productId}
                    >
                        {isUpdating ? (
                            <>
                                <Spinner size="sm" animation="border" className="me-2" />
                                {existingIngredients ? 'Updating...' : 'Saving...'}
                            </>
                        ) : (
                            existingIngredients ? 'Update Ingredients' : 'Save Ingredients'
                        )}
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default AllocateIngredients;